import { animate, stagger } from 'animejs';
import globalVar from '../utils/globalVar';

class Module00NewsQuery {
  constructor() {
    let cache = {};

    let initFunc = () => {
      initCache();
      if (!cache.section) return;
      registerEvents();
      initSticky();
    };

    let initCache = () => {
      cache.section    = document.querySelector('.module-00-news-query');
      if (!cache.section) return;
      cache.wrapper    = cache.section.querySelector('.news-wrapper');
      cache.sticky     = document.querySelector('.subscribe-sticky');
      cache.container  = document.querySelector('.page-container');
      cache.newsletter = document.querySelector('.module-16-newsletter');
    };

    let registerEvents = () => {
      // Event delegation on the section covers both server-rendered and AJAX-replaced links
      cache.section.addEventListener('click', onPaginationClick);
      // No popstate handler — page-load.js handles browser back/forward with its normal transition
    };

    // --- Sticky subscribe button ---

    let updateSticky = () => {
      if (!cache.sticky || !cache.container) return;
      const scrollY         = window.scrollY || window.pageYOffset;
      const viewBottom      = scrollY + window.innerHeight;
      const containerBottom = cache.container.getBoundingClientRect().bottom + scrollY;

      if (viewBottom >= containerBottom) {
        cache.sticky.classList.remove('is-sticky-fixed');
        cache.sticky.classList.add('is-sticky-bottom');
      } else {
        cache.sticky.classList.remove('is-sticky-bottom');
        cache.sticky.classList.add('is-sticky-fixed');
      }
    };

    let initSticky = () => {
      if (!cache.sticky) return;

      window.addEventListener('scroll', updateSticky, { passive: true });
      window.addEventListener('resize', updateSticky, { passive: true });
      updateSticky();

      if (cache.newsletter) {
        cache.sticky.addEventListener('click', () => {
          window.__smoothScroll?.scrollTo(cache.newsletter, { duration: 0.8 });
        });
      }
    };

    // --- Pagination ---

    let onPaginationClick = (e) => {
      const link = e.target.closest('[data-page]');
      if (!link || link.tagName !== 'A') return;
      // Stop the event here — page-load.js is on document and would trigger its transition
      e.preventDefault();
      e.stopPropagation();
      const page = parseInt(link.dataset.page);
      if (!page) return;
      goToPage(page);
    };

    let goToPage = (page) => {
      const nonce      = cache.section.dataset.nonce;
      const perPage    = parseInt(cache.section.dataset.perPage);
      const heroOffset = parseInt(cache.section.dataset.heroOffset);
      const baseUrl    = cache.section.dataset.baseUrl;

      const paginationEl = cache.section.querySelector('.pagination-wrapper');
      if (paginationEl) paginationEl.classList.add('is-loading');

      const currentItems = [...cache.wrapper.querySelectorAll('.news-item')];

      // AJAX runs in parallel with the exit animation for speed
      const fetchPromise = fetch(ajaxurl, {
        method: 'POST',
        body: new URLSearchParams({
          action:      'paginate_news',
          page,
          per_page:    perPage,
          hero_offset: heroOffset,
          base_url:    baseUrl,
          nonce,
        }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }).then(r => r.json());

      // ── 1. News disappear ────────────────────────────────────────────────────
      const animOutPromise = new Promise(resolve => {
        if (currentItems.length === 0) { resolve(); return; }
        animate(currentItems, {
          opacity:  [1, 0.3],
          duration: 220,
          delay:    stagger(30),
          ease:     'inQuad',
          onComplete: resolve,
        });
      });

      // Wait for both before touching the DOM — keeps layout stable for Lenis
      Promise.all([animOutPromise, fetchPromise]).then(([, res]) => {
        if (!res || !res.success) {
          if (paginationEl) paginationEl.classList.remove('is-loading');
          return;
        }

        // Swap content while items are invisible — no height animation so the
        // layout is fully settled before the scroll starts (prevents Lenis glitch)
        cache.wrapper.innerHTML = res.data.html;

        const currentPagination = cache.section.querySelector('.pagination-wrapper');
        if (currentPagination && res.data.pagination_html) {
          currentPagination.outerHTML = res.data.pagination_html;
        }

        cache.section.dataset.currentPage = page;

        const newUrl = new URL(baseUrl, window.location.origin);
        newUrl.searchParams.delete('page');
        newUrl.searchParams.delete('np');
        if (page > 1) newUrl.searchParams.set('page', page);
        history.pushState(null, '', newUrl.toString());

        const newItems = [...cache.wrapper.querySelectorAll('.news-item')];
        // Keep items fully hidden during scroll — prevents any conflict with
        // scroll-driven opacity animations while Lenis is mid-flight
        newItems.forEach(item => { item.style.opacity = '0'; });

        // ── 2. Scroll up — DOM is stable, items are hidden ───────────────────
        window.__smoothScroll?.scrollTo(cache.section, {
          duration: 1.2,
          offset:   -80,
          // ── 3. New news appear only after scroll fully completes ──────────
          onComplete: () => {
            // Reinit while all items are still at opacity 0 — scroll-driven
            // animations initialize invisibly, so there's no visible snap
            // when they set child opacities based on scroll position.
            document.dispatchEvent(new CustomEvent('scrollAnimations:reinit', { detail: { newItems } }));
            // Fade everything in after reinit has initialized
            animate(newItems, {
              opacity:  [0, 1],
              duration: 600,
              delay:    stagger(70),
              ease:     'outSine',
            });
          },
        });
      });
    };

    initFunc();
  }
}
export { Module00NewsQuery }
